import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ClipboardList, Search, Save, X, Maximize2, Minimize2, Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Icd10Box } from "./Icd10Box";
import {
  InvestigationDetailDialog, InvestigationBatchDetailsDialog,
  type InvDetailEntry, type InvDetailsMap,
} from "./InvestigationDetailsDialog";
import { InvestigationTemplatePickerDialog } from "./InvestigationTemplatePickerDialog";
import { SaveAsInvestigationTemplateDialog } from "./SaveAsInvestigationTemplateDialog";
import {
  usePublicAppSettings, useSlotBlocks,
  type Icd10Code, type Investigation, type InvestigationTemplate,
} from "@/lib/queries";
import { classifyInvestigation } from "@/lib/pdf/investigationPdf";
import { nextWorkingDay, dayUnavailableReason, DEFAULT_WEEKLY_SCHEDULE } from "@emr/shared/scheduling";
import { fmtDate, dateIST } from "@emr/shared/india";
import { useDropdownNav } from "@/hooks/useDropdownNav";

const COLUMN_CARD = "flex h-[37.5vh] min-h-[240px] flex-col";
const COLUMN_BODY = "flex flex-1 flex-col gap-3 overflow-hidden px-6 py-3";

function ColumnTitle({ children }: { children: React.ReactNode }) {
  return (
    <CardTitle className="text-sm text-maroon-700 dark:text-amber-500">{children}</CardTitle>
  );
}

function FieldArea({
  label, value, onChange, placeholder,
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1">
      <Label className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted">{label}</Label>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-h-0 flex-1 resize-none overflow-y-auto"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column 1 — Visit details: presenting complaints, findings, diagnosis
// ---------------------------------------------------------------------------

interface VisitDetailsProps {
  subjective: string;
  objective: string;
  diagnosis: string;
  onSubjective: (v: string) => void;
  onObjective: (v: string) => void;
  onDiagnosis: (v: string) => void;
}

export function VisitDetailsColumn({
  subjective, objective, diagnosis, onSubjective, onObjective, onDiagnosis,
}: VisitDetailsProps) {
  return (
    <Card className={COLUMN_CARD}>
      <CardContent className={COLUMN_BODY}>
        <FieldArea
          label="Presenting Complaints"
          value={subjective}
          onChange={onSubjective}
          placeholder="Patient's complaints in their words…"
        />
        <FieldArea
          label="Findings"
          value={objective}
          onChange={onObjective}
          placeholder="Examination findings, observations…"
        />
        <FieldArea
          label="Diagnosis"
          value={diagnosis}
          onChange={onDiagnosis}
          placeholder="Clinical impression / diagnosis…"
        />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Column 2 — ICD-10 codes, plan, additional notes (internal only)
// ---------------------------------------------------------------------------

interface CodesPlanProps {
  diagnosis: string;
  icd10Codes: Icd10Code[];
  onIcd10Codes: (codes: Icd10Code[]) => void;
  plan: string;
  onPlan: (v: string) => void;
  noteText: string;
  onNoteText: (v: string) => void;
  patientContext?: { age?: number; sex?: string };
}

export function CodesPlanColumn({
  diagnosis, icd10Codes, onIcd10Codes, plan, onPlan, noteText, onNoteText, patientContext,
}: CodesPlanProps) {
  return (
    <Card className={COLUMN_CARD}>
      <CardContent className={COLUMN_BODY}>
        <FieldArea
          label="Plan"
          value={plan}
          onChange={onPlan}
          placeholder="Treatment plan, medications, further workup…"
        />
        <div className="flex min-h-0 flex-1 flex-col gap-1">
          <Label className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted">
            Additional Notes <span className="normal-case font-normal">(internal only — never printed)</span>
          </Label>
          <Textarea
            value={noteText}
            onChange={(e) => onNoteText(e.target.value)}
            placeholder="Notes visible in the system only. Never appears on any report or printout."
            className="min-h-0 flex-1 resize-none overflow-y-auto"
          />
        </div>
        <Icd10Box
          codes={icd10Codes}
          onChange={onIcd10Codes}
          diagnosis={diagnosis}
          patientContext={patientContext}
        />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Column 3 — Investigations: search + badges + templates
// ---------------------------------------------------------------------------

interface InvestigationsProps {
  selected: string[];
  onSelected: (ids: string[]) => void;
  labNotes: string;
  radiologyNotes: string;
  electrophysiologyNotes: string;
  onLabNotes: (v: string) => void;
  onRadiologyNotes: (v: string) => void;
  onElectrophysiologyNotes: (v: string) => void;
  // Per-investigation Region & Side / Clinical Notes for Rad/EP tests.
  investigationDetails: InvDetailsMap;
  onInvestigationDetails: (details: InvDetailsMap) => void;
  // Injected from emr-visit so classifyInvestigation can categorize tests
  // without a redundant API call inside the column.
  investigations: Investigation[];
}

export function InvestigationsColumn({
  selected, onSelected,
  labNotes, radiologyNotes, electrophysiologyNotes,
  onLabNotes, onRadiologyNotes, onElectrophysiologyNotes,
  investigationDetails, onInvestigationDetails, investigations,
}: InvestigationsProps) {
  const [query, setQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Investigation templates: picker dialog (mirrors the prescription card's
  // Templates button) and save-as-template dialog (mirrors the prescription
  // card's Save as template button). Replaced the old "Add template" select
  // dropdown (user request 2026-11-02).
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [openNote, setOpenNote] = useState<"lab" | "radiology" | "electrophysiology" | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Expand the investigations card to fullscreen (excluding the navbar and
  // sidebar) so the user can search/review selected tests comfortably.
  // Toggled by clicking the "Investigations" heading; closed with the [X]
  // button or Esc.
  const [fullscreen, setFullscreen] = useState(false);
  // Pixel offset of the overlay's left edge — matches the current sidebar
  // width (w-16 = 64px collapsed, w-60 = 240px expanded) so the overlay
  // never covers the sidebar. Measured at toggle-on time.
  const [sidebarWidth, setSidebarWidth] = useState(0);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);
  useLayoutEffect(() => {
    if (!fullscreen) return;
    const aside = document.querySelector("aside");
    setSidebarWidth(aside ? aside.getBoundingClientRect().width : 0);
  }, [fullscreen]);

  // ---- Per-investigation details dialog state ----
  // Single-edit dialog: open for one investigation at a time (pencil click
  // or auto-open when a Rad/EP test is added from search).
  const [singleDialog, setSingleDialog] = useState<{
    open: boolean;
    invId: string;
    invName: string;
    invCategory: string;
  }>({ open: false, invId: "", invName: "", invCategory: "" });

  // Batch dialog: open when a template adds multiple Rad/EP tests that need
  // details collected at once.
  const [batchDialog, setBatchDialog] = useState<{
    open: boolean;
    entries: { id: string; name: string; category: string }[];
  }>({ open: false, entries: [] });

  const matches = query.trim()
    ? investigations
        .filter((i) => i.name.toLowerCase().includes(query.trim().toLowerCase()))
        .slice(0, 12)
    : [];

  // Keyboard navigation: ArrowUp/Down to move highlight, Enter to add the
  // highlighted test and return focus to the search box, Escape to close.
  const { highlight: invHighlight, highlightRef: invHighlightRef, onKeyDown: onInvSearchKeyDown } = useDropdownNav({
    count: matches.length,
    onSelect: (i) => { if (matches[i]) addTest(matches[i].id); },
    inputRef: searchRef,
    onEscape: () => setDropdownOpen(false),
  });

  /**
   * Check if an investigation is Radiology or Electrophysiology (i.e. needs
   * per-investigation Region & Side / Clinical Notes). Lab tests and any
   * other category do not need details.
   */
  const needsDetails = (inv: Investigation): boolean => {
    const cat = classifyInvestigation(inv.category);
    return cat === "Radiology" || cat === "Electrophysiology";
  };

  const addTest = (id: string) => {
    const isNew = !selected.includes(id);
    if (isNew) onSelected([...selected, id]);
    setQuery("");
    setDropdownOpen(false);
    searchRef.current?.focus();
    // Auto-open the details dialog for newly-added Rad/EP tests.
    if (isNew) {
      const inv = investigations.find((i) => i.id === id);
      if (inv && needsDetails(inv)) {
        setSingleDialog({
          open: true,
          invId: id,
          invName: inv.name,
          invCategory: inv.category ?? "",
        });
      }
    }
  };

  const removeTest = (id: string) => {
    onSelected(selected.filter((x) => x !== id));
    // Clean up any stored details for the removed test.
    if (investigationDetails[id]) {
      const next = { ...investigationDetails };
      delete next[id];
      onInvestigationDetails(next);
    }
  };

  const applyTemplate = (tpl: InvestigationTemplate) => {
    const validIds = tpl.items.filter((id) => investigations.some((i) => i.id === id));
    const merged = [...selected];
    const newlyAdded: string[] = [];
    for (const id of validIds) {
      if (!merged.includes(id)) {
        merged.push(id);
        newlyAdded.push(id);
      }
    }
    onSelected(merged);
    searchRef.current?.focus();
    // If the template added any Rad/EP tests that need details, open the
    // batch dialog to collect them all at once.
    const radEpEntries = newlyAdded
      .map((id) => investigations.find((i) => i.id === id))
      .filter((i): i is NonNullable<typeof i> => !!i && needsDetails(i))
      .map((i) => ({ id: i.id, name: i.name, category: i.category ?? "" }));
    if (radEpEntries.length > 0) {
      setBatchDialog({ open: true, entries: radEpEntries });
    }
  };

  const selectedTests = selected
    .map((id) => investigations.find((i) => i.id === id))
    .filter((i): i is NonNullable<typeof i> => !!i);

  // Save single-investigation details from the dialog.
  const handleSingleSave = (value: InvDetailEntry) => {
    onInvestigationDetails({
      ...investigationDetails,
      [singleDialog.invId]: value,
    });
  };

  // Save batch details from the batch dialog (merges with existing details).
  const handleBatchSave = (details: InvDetailsMap) => {
    onInvestigationDetails({ ...investigationDetails, ...details });
  };

  return (
    <Card
      className={fullscreen
        ? "fixed right-0 top-14 bottom-0 z-40 flex flex-col rounded-none border-0 shadow-lg"
        : COLUMN_CARD
      }
      style={fullscreen ? { left: sidebarWidth } : undefined}
    >
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setFullscreen((f) => !f)}
          title={fullscreen ? "Exit full screen" : "Expand investigations to full screen"}
          className="group flex items-center gap-1.5 rounded-md px-1 py-0.5 -mx-1 text-maroon-700 hover:bg-cream-100 hover:text-maroon-800 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-maroon-400 dark:text-amber-500 dark:hover:bg-dark-700"
        >
          <ColumnTitle>Investigations</ColumnTitle>
          {fullscreen ? <Minimize2 size={14} className="opacity-60 transition-opacity group-hover:opacity-100" /> : <Maximize2 size={14} className="opacity-60 transition-opacity group-hover:opacity-100" />}
        </button>
        {fullscreen && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 dark:hover:bg-dark-700"
            onClick={() => setFullscreen(false)}
            title="Close full screen (Esc)"
          >
            <X size={14} className="mr-1" /> Close
          </Button>
        )}
      </CardHeader>
      <CardContent className={fullscreen ? "flex flex-1 flex-col gap-3 overflow-y-auto px-6 py-3" : COLUMN_BODY}>
        <div className="relative shrink-0">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input
            ref={searchRef}
            placeholder="Search investigations… (click or Enter to add)"
            className="pl-8"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setDropdownOpen(true); }}
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
            onKeyDown={onInvSearchKeyDown}
          />
          {dropdownOpen && query.trim() && (
            <div className="visible-scrollbar absolute z-20 mt-1 max-h-60 w-full overscroll-contain overflow-y-auto rounded-md border border-cream-200 bg-cream-100 shadow-lg dark:border-dark-700 dark:bg-dark-800">
              {matches.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted">No matching investigations.</div>
              ) : (
                matches.map((i, idx) => (
                  <button
                    key={i.id}
                    type="button"
                    ref={idx === invHighlight ? invHighlightRef : undefined}
                    className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                      idx === invHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                    }`}
                    onMouseEnter={() => {/* highlight follows mouse — optional */}}
                    onMouseDown={(e) => { e.preventDefault(); addTest(i.id); }}
                  >
                    <span className="text-ink dark:text-cream-50">{i.name}</span>
                    {i.category && <span className="text-xs text-muted">{i.category}</span>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 space-y-1">
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="h-8 flex-1 text-xs"
              onClick={() => setTemplatePickerOpen(true)}
              title="Insert a stored investigation template"
            >
              <ClipboardList size={13} className="mr-1" /> Insert Template
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 flex-1 text-xs"
              onClick={() => setSaveTemplateOpen(true)}
              disabled={selected.length === 0}
              title="Save the current investigations as a named template"
            >
              <Save size={13} className="mr-1" /> Save as Template
            </Button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-1">
          <div className="flex shrink-0 items-center justify-between gap-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted">
              Selected ({selectedTests.length})
            </Label>
            {selectedTests.length > 0 && (
              <Button
                size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-600 hover:text-red-700"
                onClick={() => onSelected([])}
                title="Remove all selected investigations"
              >
                <X size={12} className="mr-0.5" /> Clear all
              </Button>
            )}
          </div>
          {selectedTests.length === 0 ? (
            <div className="rounded-md bg-cream-50 p-3 text-xs text-muted dark:bg-dark-800">
              No investigations selected. Search above or insert a template.
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
              <div className={fullscreen ? "grid grid-cols-3 gap-2" : "flex flex-wrap gap-1.5"}>
                {selectedTests.map((t) => {
                  const isRadEp = needsDetails(t);
                  const hasDetails = !!investigationDetails[t.id];
                  return (
                    <Badge
                      key={t.id}
                      variant="secondary"
                      className={`max-w-full gap-1 pr-1 ${fullscreen ? "text-base py-1 px-2.5" : ""}`}
                    >
                      <span className="min-w-0 break-words">{t.name}</span>
                      {isRadEp && (
                        <button
                          type="button"
                          className={`shrink-0 rounded-full p-0.5 hover:bg-maroon-100 ${hasDetails ? "text-maroon-600" : "text-muted"}`}
                          onClick={() => setSingleDialog({
                            open: true,
                            invId: t.id,
                            invName: t.name,
                            invCategory: t.category ?? "",
                          })}
                          aria-label={`Edit details for ${t.name}`}
                          title={hasDetails ? "Edit Region & Side / Notes" : "Add Region & Side / Notes"}
                        >
                          <Pencil size={fullscreen ? 14 : 11} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="shrink-0 rounded-full p-0.5 hover:bg-maroon-100"
                        onClick={() => removeTest(t.id)}
                        aria-label={`Remove ${t.name}`}
                      >
                        <X size={fullscreen ? 14 : 11} />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 space-y-1.5">
          <div className="flex gap-1.5">
            {([
              { key: "lab" as const, label: "Lab notes", value: labNotes },
              { key: "radiology" as const, label: "Radiology notes", value: radiologyNotes },
              { key: "electrophysiology" as const, label: "Electrophys. notes", value: electrophysiologyNotes },
            ]).map((n) => (
              <Button
                key={n.key}
                size="sm"
                variant={openNote === n.key ? "default" : "outline"}
                className={`h-7 flex-1 text-xs ${openNote === n.key ? "bg-maroon-600 text-cream-50 hover:bg-maroon-700" : ""}`}
                onClick={() => setOpenNote(openNote === n.key ? null : n.key)}
              >
                {n.label}{n.value ? " •" : ""}
              </Button>
            ))}
          </div>
          {openNote === "lab" && (
            <Textarea value={labNotes} onChange={(e) => onLabNotes(e.target.value)} placeholder="Lab notes (saved with the visit)…" className="h-20 resize-none overflow-y-auto" />
          )}
          {openNote === "radiology" && (
            <Textarea value={radiologyNotes} onChange={(e) => onRadiologyNotes(e.target.value)} placeholder="Radiology notes (saved with the visit)…" className="h-20 resize-none overflow-y-auto" />
          )}
          {openNote === "electrophysiology" && (
            <Textarea value={electrophysiologyNotes} onChange={(e) => onElectrophysiologyNotes(e.target.value)} placeholder="Electrophysiology notes (saved with the visit)…" className="h-20 resize-none overflow-y-auto" />
          )}
        </div>
      </CardContent>

      {/* Per-investigation details dialogs */}
      <InvestigationDetailDialog
        open={singleDialog.open}
        onOpenChange={(open) => setSingleDialog((p) => ({ ...p, open }))}
        investigationName={singleDialog.invName}
        investigationCategory={singleDialog.invCategory}
        value={investigationDetails[singleDialog.invId] ?? { regionAndSide: "", clinicalNotes: "" }}
        onSave={handleSingleSave}
      />
      <InvestigationBatchDetailsDialog
        open={batchDialog.open}
        onOpenChange={(open) => setBatchDialog((p) => ({ ...p, open }))}
        entries={batchDialog.entries}
        existingDetails={investigationDetails}
        onSave={handleBatchSave}
      />

      {/* Investigation template dialogs (mirrors the prescription card) */}
      <InvestigationTemplatePickerDialog
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        investigations={investigations}
        onPick={applyTemplate}
      />
      <SaveAsInvestigationTemplateDialog
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        items={selected}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Review bar — compact row under the prescription
// ---------------------------------------------------------------------------

interface ReviewBarProps {
  visitId: string;
  reviewNeeded: boolean;
  reviewDate: string; // YYYY-MM-DD
  onReviewNeeded: (v: boolean) => void;
  onReviewDate: (v: string) => void;
}

export function ReviewBar({ visitId, reviewNeeded, reviewDate, onReviewNeeded, onReviewDate }: ReviewBarProps) {
  const { data: settings } = usePublicAppSettings();
  const { data: blocksData } = useSlotBlocks();

  const weeklySchedule = (settings?.weeklySchedule as never) ?? DEFAULT_WEEKLY_SCHEDULE;
  const slotBlocks = (blocksData?.rows ?? []).map((b) => ({
    date: typeof b.date === "string" ? b.date : dateIST(b.date as unknown as string),
    slot: b.slot,
    maxOverride: b.maxOverride,
    startOverride: b.startOverride,
    reason: b.reason,
  }));

  // Auto-default the review date to +15 working days for new visits that have
  // no review date and are not PRN. Runs once per visit (tracked by a ref so
  // a user manually clearing the date isn't immediately re-filled), and only
  // after the scheduling data has loaded so nextWorkingDay can skip blocked days.
  const defaultedVisit = useRef<string | null>(null);
  useEffect(() => {
    // Reset the guard on a visit change so a different visit can be defaulted.
    if (defaultedVisit.current !== visitId) {
      defaultedVisit.current = null;
    }
    if (defaultedVisit.current) return; // already defaulted this visit
    if (reviewNeeded) return; // PRN — no date needed.
    if (reviewDate) return; // already has a date.
    // Wait for the scheduling data to load; without it nextWorkingDay can't
    // see slot_blocks (holidays/overrides) and may land on a blocked day.
    if (!settings || !blocksData) return;
    const d = nextWorkingDay(new Date(), 15, weeklySchedule, slotBlocks);
    if (!d) return;
    onReviewDate(dateIST(d));
    defaultedVisit.current = visitId;
  }, [visitId, reviewNeeded, reviewDate, settings, blocksData, weeklySchedule, slotBlocks, onReviewDate]);

  const chips = [5, 10, 15, 30, 60, 90, 120, 180];
  const applyChip = (n: number) => {
    const d = nextWorkingDay(new Date(), n, weeklySchedule, slotBlocks);
    if (d) onReviewDate(dateIST(d));
  };

  // Why the manually-picked date is unusable (null = fine). Toast-style inline
  // warning next to the picker so the doctor sees it BEFORE save; the server
  // (PATCH /visits/:id/review) remains the authoritative guard (422).
  const unavailable = !reviewNeeded && reviewDate
    ? dayUnavailableReason(new Date(reviewDate + "T00:00:00"), weeklySchedule, slotBlocks)
    : null;

  // reviewNeeded=true → PRN ("review only if the patient needs it"):
  // the date picker and quick chips are grayed out and not clickable.
  // reviewNeeded=false → scheduled review: pick a date or use a +Nd chip.
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-cream-200 bg-cream-50/50 px-3 py-2 dark:border-dark-700 dark:bg-dark-800/50">
      <div className="flex items-center gap-2">
        <Checkbox
          id="review-needed"
          checked={reviewNeeded}
          onCheckedChange={(v) => onReviewNeeded(v === true)}
        />
        <Label htmlFor="review-needed" className="text-sm">Review if needed</Label>
      </div>

      <div className={`flex flex-wrap items-center gap-2 transition-opacity ${reviewNeeded ? "pointer-events-none opacity-40" : ""}`} aria-disabled={reviewNeeded}>
        <Input
          type="date"
          value={reviewDate}
          onChange={(e) => onReviewDate(e.target.value)}
          className="h-8 w-40"
          disabled={reviewNeeded}
        />
        {unavailable && (
          <span className="text-xs font-medium text-red-600">
            {unavailable.kind === "blocked"
              ? `Blocked${unavailable.reason ? ` — ${unavailable.reason}` : ""}`
              : "Not a working day"}
          </span>
        )}
        <div className="flex flex-wrap gap-1">
          {chips.map((n) => {
            const d = nextWorkingDay(new Date(), n, weeklySchedule, slotBlocks);
            const label = d ? `+${n}d → ${fmtDate(d)}` : `+${n}d`;
            return (
              <Button key={n} size="sm" variant="outline" className="h-7 text-xs" onClick={() => applyChip(n)} disabled={reviewNeeded}>
                {label}
              </Button>
            );
          })}
        </div>
      </div>

      {reviewNeeded && (
        <span className="text-xs text-muted">Review only if the patient needs it — no review appointment scheduled.</span>
      )}
    </div>
  );
}
